import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type UserRole = 'super_admin' | 'org_admin' | 'brand_admin' | 'brand_user' | 'care_manager' | 'care_coordinator' | 'patient' | 'cbo_staff';

interface UserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: UserRole; // Made optional as role now fetched separately
  brand_id?: string;
  organization_id?: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  canAccessBrandAdmin: () => boolean;
  canAccessSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener with guaranteed loading completion
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        console.log('🔍 Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch actual user profile from database
          fetchUserProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        // Handle session errors (like expired tokens)
        if (error) {
          console.error('Session error:', error);
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('🔍 Fetching user profile for existing session:', session.user.id);
          await fetchUserProfile(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      // Add timeout for better demo performance
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
      
      // Fetch user profile WITHOUT role (role is now in user_roles table)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, brand_id, organization_id, avatar_url, role')
        .eq('id', userId)
        .abortSignal(controller.signal)
        .maybeSingle();

      clearTimeout(timeoutId);

      if (error) {
        // If no profile exists for this user, create a default one
        if (error.code === 'PGRST116') {
          await createUserProfile(userId, user?.email);
          return; // createUserProfile will handle setting loading to false
        }
        
        console.error('Error fetching user profile:', error);
        console.error('❌ Database error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // On network failure, try to fetch from user_roles table directly as fallback
        try {
          console.log('⚠️ Attempting fallback to user_roles table...');
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role, brand_id, organization_id')
            .eq('user_id', userId)
            .maybeSingle();
          
          if (roleData) {
            // Create minimal profile with role info
            const minimalProfile: UserProfile = {
              id: userId,
              email: user?.email || '',
              role: roleData.role as UserRole,
              brand_id: roleData.brand_id || undefined,
              organization_id: roleData.organization_id || undefined
            };
            console.log('✅ Loaded minimal profile from user_roles:', minimalProfile);
            setProfile(minimalProfile);
            setLoading(false);
            return;
          }
        } catch (roleError) {
          console.error('Failed to fetch from user_roles:', roleError);
        }
        
        setProfile(null);
        setLoading(false);
        return;
      }

      if (data) {
        // Fetch all user roles and select the highest privilege
        const { data: rolesData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);

        if (roleError) {
          console.error('❌ Error fetching user role:', roleError);
        }

        console.log('🔍 Fetched roles from user_roles:', rolesData);
        console.log('🔍 User profile data:', data);

        // Define role hierarchy (higher index = higher privilege)
        const roleHierarchy = ['patient', 'cbo_staff', 'care_coordinator', 'care_manager', 'brand_user', 'brand_admin', 'org_admin', 'super_admin'];
        
        let mappedRole: UserRole = 'brand_user'; // default
        
        // If user has roles in user_roles table, use the highest one
        if (rolesData && rolesData.length > 0) {
          const highestRole = rolesData.reduce((highest, current) => {
            const currentIndex = roleHierarchy.indexOf(current.role);
            const highestIndex = roleHierarchy.indexOf(highest.role);
            return currentIndex > highestIndex ? current : highest;
          }, rolesData[0]);

          console.log('🔍 Highest role from user_roles:', highestRole);

          // Map app_role to user_role
          if (highestRole?.role) {
            switch (highestRole.role) {
              case 'super_admin':
                mappedRole = 'super_admin';
                break;
              case 'org_admin':
                mappedRole = 'brand_admin'; // org_admin maps to brand_admin for now
                break;
              case 'brand_admin':
                mappedRole = 'brand_admin';
                break;
              default:
                mappedRole = 'brand_user';
            }
          }
        } else {
          // Fallback: If no roles in user_roles table, use the role from user_profiles
          console.log('⚠️ No roles found in user_roles, falling back to user_profiles role');
          if (data.role) {
            mappedRole = data.role as UserRole;
          }
        }

        console.log('🔍 Final mapped role:', mappedRole);

        const profileData = {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          role: mappedRole,
          brand_id: data.brand_id,
          organization_id: data.organization_id,
          avatar_url: data.avatar_url,
        };
        setProfile(profileData);
      } else {
        setProfile(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Exception fetching user profile:', error);
      setProfile(null);
      setLoading(false);
    }
  };

  const createUserProfile = async (userId: string, email?: string) => {
    try {
      // Create user profile WITHOUT role
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          email: email,
          is_active: true
        })
        .select('id, email, first_name, last_name, brand_id, organization_id, avatar_url')
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        setLoading(false);
        return;
      }

      // Create default role in user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: 'brand_user'
        });

      if (roleError) {
        console.error('Error creating user role:', roleError);
      }

      if (data) {
        setProfile({
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          role: 'brand_user' as UserRole,
          brand_id: data.brand_id,
          organization_id: data.organization_id,
          avatar_url: data.avatar_url,
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Exception creating user profile:', error);
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('🔐 SignOut: Starting signout process...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('🔐 SignOut: Error from Supabase signOut:', error);
        toast.error('Failed to sign out');
        return;
      }
      
      console.log('🔐 SignOut: Supabase signOut successful, clearing local state...');
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setProfile(null);
      setLoading(false);
      
      console.log('🔐 SignOut: Local state cleared, forcing page reload...');
      toast.success('Signed out successfully');
      
      // Force a clean reload to ensure all state is cleared
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (error) {
      console.error('🔐 SignOut: Exception during sign out:', error);
      toast.error('Failed to sign out');
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return profile?.role === role;
  };

  const canAccessBrandAdmin = (): boolean => {
    return profile?.role === 'super_admin' || 
           profile?.role === 'org_admin' || 
           profile?.role === 'brand_admin';
  };

  const canAccessSuperAdmin = (): boolean => {
    console.log('🔍 canAccessSuperAdmin check - profile:', profile);
    console.log('🔍 canAccessSuperAdmin check - profile.role:', profile?.role);
    const hasAccess = profile?.role === 'super_admin';
    console.log('🔍 canAccessSuperAdmin result:', hasAccess);
    return hasAccess;
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signOut,
    hasRole,
    canAccessBrandAdmin,
    canAccessSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};